import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

import { useTranslation } from 'react-i18next';
const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_DURATION_DAYS = 7;

function isDismissed() {
  try {
    const val = localStorage.getItem(DISMISS_KEY);
    if (!val) return false;
    const parsed = JSON.parse(val);
    const now = Date.now();
    const expires = parsed + DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;
    if (now > expires) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {}
}

export default function PWAInstallBanner({ installPromptEvent }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (installPromptEvent && !isDismissed()) {
      // Small delay so it doesn't pop immediately on page load
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [installPromptEvent]);

  const handleInstall = async () => {
    if (!installPromptEvent) return;
    try {
      installPromptEvent.prompt();
      const result = await installPromptEvent.userChoice;
      setOutcome(result.outcome);
      if (result.outcome === 'accepted') {
        setVisible(false);
      }
    } catch {
      // prompt() can only be called once; ignore
    }
  };

  const handleDismiss = () => {
    setDismissed();
    setVisible(false);
  };

  if (!visible || !installPromptEvent) return null;

  return (
    <div
      role="dialog"
      aria-label={t('p_w_a_install_banner.install_app')}
      className="fixed bottom-0 left-0 right-0 z-[200] bg-zinc-950 border-t border-amber-500/30 shadow-[0_-4px_30px_rgba(245,158,11,0.15)] animate-slide-up"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-amber-500" />
          </div>
          <div className="min-w-0">
            <p className="font-display text-sm uppercase text-white truncate">{t('p_w_a_install_banner.install_max_os')}</p>
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{t('p_w_a_install_banner.add_to_home_screen_for_the_full_experience')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleInstall}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Install
          </button>
          <button
            onClick={handleDismiss}
            aria-label={t('p_w_a_install_banner.dismiss_install_prompt')}
            className="p-2 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {outcome === 'dismissed' && (
        <div className="text-center pb-2 font-mono text-[9px] text-zinc-600 uppercase tracking-wider">
          You can install later from your browser menu
        </div>
      )}
    </div>
  );
}
